import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { theBodyOf } from '../../../utils/what-the-api-returns';

/** The signed-in participant's in-app balance. Amounts are in cents. */
export interface WalletBalance {
  balance: number;
  currency: string;
  totalCredited: number;
  totalSpent: number;
  /**
   * The part of `balance` that is Premium credit still valid, valid 12 months
   * from when it was received. Absent from an API older than 15 October 2026.
   */
  premiumCredit?: number;
}

export type WalletMovementDirection = 'credit' | 'debit';
export type WalletMovementReason =
  | 'refund'
  | 'session_payment'
  | 'admin_adjustment'
  | 'reversal'
  | 'premium_credit'
  | 'credit_expiry'
  | 'account_deletion_forfeit'
  | 'account_deletion_card_refund'
  | 'account_deletion_manual_refund_due';

/** One line of wallet history. Amounts in cents; `amount` is always positive. */
export interface WalletMovement {
  _id: string;
  direction: WalletMovementDirection;
  amount: number;
  balanceAfter: number;
  currency: string;
  reason: WalletMovementReason;
  relatedEventId?: string;
  createdAt?: string;
}

export interface WalletMovementsPage {
  items: WalletMovement[];
  total: number;
}

/**
 * Reads the consumer wallet (balance + history).
 *
 * Called directly rather than through the generated client, like
 * CancelActivityService: these endpoints are not in the generated SDK, and
 * regenerating it from a running API rewrites hundreds of files. Auth is added
 * by the token interceptor.
 */
@Injectable({ providedIn: 'root' })
export class WalletService {
  private readonly http = inject(HttpClient);

  getWallet(): Observable<WalletBalance> {
    return this.http.get<WalletBalance>(`${environment.api}/api/v1/wallet/me`);
  }

  getMovements(skip = 0, limit = 20): Observable<WalletMovementsPage> {
    return this.http.get<WalletMovementsPage>(`${environment.api}/api/v1/wallet/me/movements`, {
      params: { skip: String(skip), limit: String(limit) },
    });
  }

  /**
   * Pay for a session from the in-app balance. Hits the same deposit endpoint
   * as a card payment, with paymentSource 'wallet'; the server debits the
   * balance and completes the registration without Stripe. The response is
   * unwrapped like every other route (some send the payload, some an envelope).
   */
  payFromBalance(eventId: string): Observable<WalletDepositResult | undefined> {
    return this.http
      .post(`${environment.api}/api/v1/payments/process-deposit`, {
        eventId,
        paymentSource: 'wallet',
      })
      .pipe(map((res) => theBodyOf<WalletDepositResult>(res)));
  }
}

export interface WalletDepositResult {
  transactionId?: string;
  status?: string;
  paymentIntentClientSecret?: string;
}
